// The audit events that name a secret: created, replaced, opened by the broker for a run.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { formatClock, formatHourMinute, useT } from "../i18n";
import { dayMonth, shortId } from "../pages/format";
import { WidgetCard } from "../ui/Card";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { credentialUsageQuery } from "./api";
import type { Credential } from "./templates";

/** "19:47:12" today, "28 Sep 19:47" before. */
function stamp(ms: number, now = new Date()): string {
  const d = new Date(ms);
  return d.toDateString() === now.toDateString() ? formatClock(ms) : `${dayMonth(d)} ${formatHourMinute(ms)}`;
}

export function UsageLog({ credential, className }: { credential: Credential; className?: string }) {
  const t = useT().secrets.usage;
  const query = useQuery(credentialUsageQuery(credential.id));
  const count = query.data?.length;
  return (
    <WidgetCard
      className={className}
      label={t.label(credential.name)}
      title={t.title(<span className="font-mono">{credential.name}</span>)}
      description={t.description}
      action={count !== undefined ? <Pill tone="gray">{t.events(count)}</Pill> : null}
    >
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState message={query.error.message} /> : null}
      {count === 0 ? <EmptyState compact title={t.notUsed}>{t.notUsedBody}</EmptyState> : null}
      {count ? (
        <ul className="m-0 list-none divide-y divide-muted p-0">
          {query.data?.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 py-2.5 text-[13px]">
              <Mono className="w-24 shrink-0 text-gray-500">{stamp(u.at)}</Mono>
              <span className="font-mono text-gray-900">{u.action}</span>
              {u.run_id ? (
                <Link to={`/runs/${u.run_id}`} className="ms-auto text-gray-500 hover:text-primary">
                  {t.run(shortId(u.run_id))}
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </WidgetCard>
  );
}
